import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, of } from 'rxjs';

import { environment } from '@env/environment';

import { VersionCheckerService } from './version-checker.service';

export interface DeployVersionInfo {
  label: string;
  version: string;
  component: 'fe' | 'be';
  environment: string;
  branch: string;
  commit: string;
  builtAt: string;
  runtimeEnv?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DeployVersionService {
  private readonly http = inject(HttpClient);
  private readonly versionChecker = inject(VersionCheckerService);

  getFrontendVersion(): DeployVersionInfo {
    return this.getFrontendVersionFromBundle();
  }

  loadFrontendVersion(): Observable<DeployVersionInfo> {
    if (!environment.production) {
      return of(this.getFrontendVersionFromBundle());
    }

    return this.http.get<DeployVersionInfo>('/deploy-version.json').pipe(
      catchError(() => of(this.getFrontendVersionFromBundle()))
    );
  }

  getBackendVersion(): Observable<DeployVersionInfo | null> {
    return this.http.get<DeployVersionInfo>(`${environment.backendURL}/api/status/deploy`).pipe(
      catchError(() => of(null))
    );
  }

  formatBuiltAt(isoDate: string): string {
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) {
      return isoDate;
    }

    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  }

  private getFrontendVersionFromBundle(): DeployVersionInfo {
    const version = this.versionChecker.getCurrentVersion();
    const isDevelopment = version === '__BUILD_VERSION__';

    if (isDevelopment) {
      return {
        label: 'oneray.fe',
        version: 'oneray.fe.dev+local',
        component: 'fe',
        environment: environment.envName ?? 'dev',
        branch: 'dev',
        commit: 'local',
        builtAt: new Date().toISOString(),
      };
    }

    return this.parseVersionString(version, 'fe', environment.envName ?? 'unknown');
  }

  private parseVersionString(
    version: string,
    component: 'fe' | 'be',
    fallbackEnvironment: string
  ): DeployVersionInfo {
    const match = /^oneray\.(fe|be)\.(\d{8}\.\d{4})\+([0-9a-f]+)$/i.exec(version);

    if (!match) {
      return {
        label: `oneray.${component}`,
        version,
        component,
        environment: fallbackEnvironment,
        branch: fallbackEnvironment,
        commit: version,
        builtAt: '',
      };
    }

    const [, , stamp, commit] = match;
    const year = stamp.slice(0, 4);
    const month = stamp.slice(4, 6);
    const day = stamp.slice(6, 8);
    const hours = stamp.slice(9, 11);
    const minutes = stamp.slice(11, 13);

    return {
      label: `oneray.${component}`,
      version,
      component,
      environment: fallbackEnvironment,
      branch: fallbackEnvironment,
      commit,
      builtAt: new Date(`${year}-${month}-${day}T${hours}:${minutes}:00.000Z`).toISOString(),
    };
  }
}
